import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild,} from '@angular/core';
import {
  concatMap,
  finalize,
  forkJoin,
  from,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
  toArray,
  zip,
} from 'rxjs';
import {PersonUtils} from 'src/app/auth_profile/utils/v3/person-utils';
import {
  SimpleLessonScheduleRequest as SimpleLessonScheduleRequestV3,
  SimpleTeacherProductAvailability,
  StudentColLessonScheduleEvents,
} from '../../../model/v3/booking';
import {AppEventsService} from 'src/app/col/services/app-events.service';
import {ErrorBase} from 'src/app/model/ErrorBase';
import {Dates} from 'src/app/utils/calendar-utils';
import {ModalComponent} from 'src/app/utils/modal/modal.component';
import {Page, Pageable} from 'src/app/utils/pageable';
import {CalendarEntry, ClickEvent, DateRange, DateRangeSet} from '../../../model/calendar';
import {ActivateCourseModalComponent} from "../../../../utils/activate-course-modal/activate-course-modal.component";
import {Router} from '@angular/router';
import {ProductContextService} from 'src/app/col/services/product-context.service';
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";
import {catchError} from "rxjs/operators";
import * as LessonsV3 from "../../../model/v3/lessons";
import {PersonContactCacheService} from "../../../services/person-contact-cache.service";
import {AuthorizationServiceProvider} from "../../../../auth_profile/services/authorization-service.provider";
import {Utils} from "../../../utils/lesson-utils";
import {CourseProductMapper} from "../../../utils/course-product-mapper";
import {V3SchoolTeacher} from "../../../../auth_profile/model/v3/schools";

@Component({
    selector: 'app-student-main-calendar',
    templateUrl: './student-main-calendar.component.html',
    styleUrls: ['./student-main-calendar.component.scss'],
    standalone: false
})
export class StudentMainCalendarComponent implements OnInit, OnDestroy {
  private static readonly DAY_MS = 24 * 60 * 60 * 1000;
  private static readonly LESSON_DURATION_MS = 30 * 60 * 1000;
  @ViewChild('scheduleLessonModal', { static: true })
  scheduleLessonModal: ModalComponent;
  @ViewChild('noCreditsModal', { static: true })
  noCreditsModal: ModalComponent;
  @ViewChild('reserveErrorModal', { static: true })
  reserveErrorModal: ModalComponent;
  @ViewChild('activateCourseModalEvent', { static: true })
  activateCourseModalEvent: ActivateCourseModalComponent;
  @Output() scheduleDateEmitter = new EventEmitter<Date>()
  @Output() openFilterModal = new EventEmitter();

  _studentId: number;
  _langCode: string;
  private teachersIdFilter: number[];
  private _focusDate: Date;

  events: CalendarEntry[];
  availableWorktimes: SimpleTeacherProductAvailability[] = [];
  schedules: StudentColLessonScheduleEvents[];
  requiredCompetences: string[];
  loading: boolean = true;
  studentProductContext: LessonsV3.ProductContextDetails | null = null;
  private currentCourseCode: string | null = null;
  private schoolId: number | null = null;
  credits = 0;
  creditsSubscription: Subscription | null = null;
  // lesson schedule variables
  _slScheduleDate: Date;
  _slTeacherIds: number[];
  _slTeacherProfiles: { [id: number]: V3SchoolTeacher } = {};
  _slTeacherCourseProfiles: { [id: number]: LessonsV3.V3CourseTeacherProfile[] } = {};
  _slShowTeachers = false;
  _slPreferredTeacher: number = null;
  _slScheduleFinishDate: Date;
  _slSelectedWorktimes: SimpleTeacherProductAvailability[];
  _slShowAll = true;
  availableTeacherIds: number[]
  _errorMessage: string;

  productContextSubscription: Subscription

  @Input()
  set filterTeachersIds(teachersIds: number[]) {
    this.teachersIdFilter = teachersIds;
    this.remapCalendarEntries();
  }

  @Input()
  set studentId(studentId: number) {
    this._studentId = studentId;
    this.triggerReload(false);
  }

  @Input()
  set langCode(langCode: string) {
    if(langCode == '') {
      this.loading = false
      return
    }
    this._langCode = langCode;
    this.triggerReload(true);
  }

  set focusDate(date: Date) {
    this._focusDate = date;
    this.loadCalendarEvents();
  }

  @Output()
  availableTeachersIdsUpdate = new EventEmitter<number[]>();

  constructor(
    private appEvents: AppEventsService,
    private router: Router,
    private productContextService: ProductContextService,
    private apiProvider: NativeServiceApiProvider,
    private personContactCache: PersonContactCacheService,
    private authProvider: AuthorizationServiceProvider
  ) {}

  ngOnInit(): void {
    this.productContextSubscription = this.productContextService.productContext.pipe(
      tap(() => (this.requiredCompetences = null)),
      switchMap(() => this.getProductContext()),
      switchMap(context => this.getCourseCode(context)),
      switchMap(() => this.getCredits()),
      tap(() => this.loadCalendarEvents()),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.productContextSubscription?.unsubscribe();
    this.creditsSubscription?.unsubscribe();
  }

  private ensureSchoolId(): Observable<number> {
    if (this.schoolId != null) {
      return of(this.schoolId);
    }
    return this.authProvider.getAuthDetailsService().pipe(
      switchMap((authApi) => authApi.getSelfSchoolId()),
      tap((schoolId) => (this.schoolId = schoolId))
    );
  }

  getCredits(): Observable<Page<LessonsV3.LessonBundle>> {
    if (!this._studentId || !this._langCode) {
      return of(null);
    }
    return this.ensureSchoolId().pipe(
      switchMap((schoolId) =>
        this.apiProvider.colV3().pipe(
          switchMap((api) =>
            api.getStudentBundles(
              schoolId,
              this._studentId,
              Pageable.of(0, 1000, null),
              this._langCode,
              true
            )
          )
        )
      ),
      tap((result) => {
        this.credits = (result?.content ?? []).reduce(
          (total, bundle) => total + bundle.available,
          0
        );
        this.appEvents.credits(this._langCode).next(this.credits);
      })
    );
  }

  subscribeForCredits(): void {
    if (!this._langCode) {
      return;
    }
    this.creditsSubscription?.unsubscribe();
    this.creditsSubscription = this.appEvents
      .credits(this._langCode)
      .subscribe((credits) => (this.credits = credits));
  }

  loadCalendarEvents() {
    if (!this._studentId || !this._langCode || !this._focusDate) {
      return;
    }
    this.loading = true;
    this.loadRequiredCompetences()
      .pipe(
        switchMap(() =>
          zip(
            this.getTeachersWeekAvailability(),
            this.getStudentWeekSchedules()
          )
        ),
        tap(() => this.remapCalendarEntries()),
        catchError(() => {
          this.availableWorktimes = [];
          this.schedules = [];
          this.events = [];
          return of(null);
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe();
  }

  private loadRequiredCompetences(): Observable<string[]> {
    if (this.requiredCompetences && this.requiredCompetences.length > 0) {
      return of(this.requiredCompetences);
    }
    return this.getProductContext().pipe(
      switchMap((context) => this.getCourseCode(context)),
      switchMap((code) => this.getCompetences(code)),
      map((competences) => (competences ?? []).map((competence) => competence.code)),
      tap((codes) => (this.requiredCompetences = codes))
    );
  }

  private refreshBaseData(loadCredits: boolean): Observable<null> {
    return this.getProductContext().pipe(
      switchMap(() => (loadCredits ? this.getCredits() : of(null))),
      map(() => null)
    );
  }

  getProductContext(): Observable<LessonsV3.ProductContextDetails | null> {
    if (!this._studentId || !this._langCode)
      return of(null)

    return this.ensureSchoolId().pipe(
      switchMap((schoolId) =>
        this.apiProvider.colV3().pipe(
          switchMap((api) =>
            api.getProductContexts(schoolId, this._studentId, this._langCode)
          )
        )
      ),
      map((contexts) => contexts?.[0] ?? null),
      tap((context) => (this.studentProductContext = context))
    );
  }

  getCourseCode(context: LessonsV3.ProductContextDetails | null): Observable<string> {
    if(!context)
      return of(null)
    return this.ensureSchoolId().pipe(
      switchMap((schoolId) =>
        this.apiProvider.colV3().pipe(
          switchMap((api) =>
            api.getCourseContexts(schoolId, this._studentId, this._langCode, true).pipe(
              map((contexts: LessonsV3.ColCourseContext[] | null) => contexts?.[0]?.course?.code ?? `${this._langCode}.s1`)
            )
          )
        )
      ),
      tap((courseCode) => {
        this.currentCourseCode = courseCode;
      })
    );
  }

  getCompetences(courseCode: string): Observable<LessonsV3.CompetenceRef[]> {
    const competencesFromLessonType =
      this.mapContextLessonTypeOnRequiredCompetences();
    if (competencesFromLessonType) {
      return of(competencesFromLessonType);
    }
    if (!courseCode || !this._langCode) {
      return of([]);
    }
    return this.apiProvider.colV3().pipe(
      switchMap((api) => api.getCompetenceMappings()),
      map((mappings) =>
        (mappings ?? [])
          .filter((mapping) => mapping?.courseCode === courseCode)
          .map((mapping) => mapping?.competenceCode)
          .filter((code): code is string => !!code)
      ),
      map((codes) => Array.from(new Set(codes))),
      map((codes) => codes.map((code): LessonsV3.CompetenceRef => ({code})))
    );
  }

  getTeachersWeekAvailability(): Observable<null> {
    const {weekStart, weekEnd} = this.getWeekRange();
    return this.ensureSchoolId().pipe(
      switchMap((schoolId) =>
        this.apiProvider.colV3().pipe(
          switchMap((api) =>
            api.getTeacherAvailability(
              schoolId,
              this._studentId,
              weekStart,
              weekEnd,
              this._langCode
            )
          )
        )
      ),
      map((worktimes: SimpleTeacherProductAvailability[]) =>
        worktimes.filter((schedule) =>
          schedule.events.some(
            (e) => e.eventDate.getTime() + e.duration >= Date.now()
          )
        )
      ),
      map((worktimes: SimpleTeacherProductAvailability[]) =>
        worktimes.filter((wt) => {
          const wtCompetences = wt.details.competences.map(c => c.code );
          return wt.details.product.code === this._langCode && wtCompetences.some( c => this.requiredCompetences?.includes(c));
        })
      ),
      tap((worktimes: SimpleTeacherProductAvailability[]) => {
        this.collectAvailableTeacherIds(worktimes);
        this.availableWorktimes = worktimes;
      }),
      switchMap(() => this.prepareTeachersProfiles(this.availableTeacherIds)),
      switchMap(teachersProfiles => this.loadTeachersPhotos(teachersProfiles))
    )
  }

  loadTeachersPhotos(teachersProfiles: V3SchoolTeacher[]): Observable<null> {
    if (!teachersProfiles || teachersProfiles.length === 0) return of(null);
    return forkJoin(
      (teachersProfiles ?? []).map((profile) =>
        this.personContactCache.getPhotoUrl(profile?.person?.id).pipe(map(() => null))
      )
    ).pipe(map(() => null));
  }

  getStudentWeekSchedules(): Observable<StudentColLessonScheduleEvents[]> {
    const {weekStart, weekEnd} = this.getWeekRange();
    return this.ensureSchoolId().pipe(
      switchMap((schoolId) =>
        this.apiProvider.colV3().pipe(
          switchMap((api) =>
            api.getStudentLessonSchedules(
              schoolId,
              this._studentId,
              weekStart,
              weekEnd,
              this._langCode
            )
          )
        )
      ),
      tap((schedules) => (this.schedules = schedules))
    );
  }

  remapCalendarEntries() {
    const schedulesEvents = (this.schedules ?? [])
      .filter((schedule) => !!schedule?.schedule && !!schedule?.events?.length)
      .map((schedule) => this.mapScheduleToCalendarEntry(schedule))
      .filter((entry): entry is CalendarEntry => !!entry);

    let worktimes = [...(this.availableWorktimes ?? [])];
    if (this.teachersIdFilter && this.teachersIdFilter.length > 0) {
      worktimes = worktimes.filter(
        (wt) => this.teachersIdFilter.includes(wt.teacher.id)
      );
    }
    const availabilities = this.mapAvailabilitiesToCalendarEntries(worktimes);

    this.events = availabilities.concat(schedulesEvents);
  }

  collectAvailableTeacherIds(worktimes: SimpleTeacherProductAvailability[]): void {
    this.availableTeacherIds = Array.from(
      new Set(worktimes.map((wt) => wt.teacher.id))
    );
    this.availableTeachersIdsUpdate.emit(this.availableTeacherIds);
  }

  mapScheduleToCalendarEntry(schedule: StudentColLessonScheduleEvents): CalendarEntry | null {
    const firstEvent = schedule?.events?.[0];
    if (!schedule?.schedule || !firstEvent?.eventDate) {
      return null;
    }
    const dateTo = new Date();
    const dateFrom = firstEvent.eventDate;
    const duration = firstEvent.duration ?? 0;
    dateTo.setTime(dateFrom.getTime() + duration);
    const title = schedule.schedule.name;

    return new CalendarEntry(schedule.schedule.id, 1, dateFrom, dateTo, () => title, null, schedule, 'schedule');

  }

  mapAvailabilitiesToCalendarEntries(
    worktimes: SimpleTeacherProductAvailability[]
  ): CalendarEntry[] {
    const rangeSet = new DateRangeSet();

    for (const wt of worktimes) {
      for (const ev of wt.events) {
        // create range set which holds just the worktime range
        const wtRangeSet = new DateRangeSet();
        const wtDateRange = new DateRange();
        wtDateRange.dateFrom = new Date(ev.eventDate);
        wtDateRange.dateTo = new Date(wtDateRange.dateFrom.getTime() + ev.duration);
        wtRangeSet.add(wtDateRange);

        // create overtake range
        const overtakeRange = new DateRange();
        overtakeRange.dateFrom = new Date(0);
        if (wt.details.overtake == null) {
          wt.details.overtake = 0;
        }
        overtakeRange.dateTo = Dates.roundDateToNextHalfHour(new Date(new Date().getTime() + 60000 * 30 + wt.details.overtake));

        // subtract overtake and add results to the final range set
        wtRangeSet.sub(overtakeRange);
        wtRangeSet.orderedRanges.forEach(r => rangeSet.add(r));
      }
    }

    return rangeSet.orderedRanges.map(range => {
      return new CalendarEntry(null, 1, range.dateFrom, range.dateTo, null, null, range, 'worktime');
    })
  }

  private mapContextLessonTypeOnRequiredCompetences(): LessonsV3.CompetenceRef[] {
    if (
      !this.studentProductContext ||
      !this.studentProductContext.nextLessonType
    )
      return null;

    const lessonType = this.studentProductContext.nextLessonType;
    if (lessonType === LessonsV3.LessonType.Standard) {
      return null;
    }
    if (lessonType === LessonsV3.LessonType.Prova) {
      return [{ code: 'PROVA' }];
    }
    return null;
  }

  onCalendarClick(event: ClickEvent) {
    if(event?.entry == null) return;
    if(!this.isCourseActive() && event?.entry?.dateFrom) {
      this._slScheduleDate = Dates.roundDateIntoMiddleOfClick(event.entry.dateFrom);
      this.activateCourseModalEvent.activateCourseModal.show()
      return;
    }
    if (event.entry != null && event.entry.relatedObject instanceof DateRange) {
      if (event.clickDate.getTime() < new Date().getTime() + 60000 * 30) {
        return;
      }
      this._slSelectedWorktimes = this.filterWorktimeByEventDate(
        event.clickDate
      );
      let teacherToSearch = Array.from(new Set(this._slSelectedWorktimes.map(wt => wt.teacher.id)))
      if (this.teachersIdFilter?.length > 0) {
        teacherToSearch = teacherToSearch.filter((teacherId) =>
          this.teachersIdFilter.includes(teacherId)
        );
        this._slShowTeachers = true;
        this._slShowAll = false;
        this._slPreferredTeacher = this.teachersIdFilter[0];
      }
      this._slTeacherIds = teacherToSearch;
      this.openLessonScheduleDialog(event.clickDate);
    }
    if (event.entry.classes?.[0]?.includes("schedule")) {
      const lessonId = event.entry?.relatedObject?.events?.[0]?.eventData?.lesson?.lessonId;
      if (!lessonId) {
        return;
      }
      this.router.navigateByUrl(
        `student/col-lessons/school/1/student/${this._studentId}/lesson/${lessonId}`
      ).then()
    }
  }

  prepareTeachersProfiles(teacherToSearch: number[]): Observable<V3SchoolTeacher[]> {
    if (!teacherToSearch?.length) return of([]);
    return this.ensureSchoolId().pipe(
      switchMap((schoolId) =>
        this.apiProvider.casa().pipe(
          switchMap((api) =>
            api.getTeachers(schoolId, null, null, teacherToSearch, Pageable.of(0, 500, null))
          ),
          map((page) => page?.content ?? [])
        )
      ),
      switchMap((teachers) => {
        if (!teachers?.length) {
          return of([] as V3SchoolTeacher[]);
        }
        return from(teachers).pipe(
          concatMap((teacher) =>
            this.apiProvider.colV3().pipe(
              switchMap((api) =>
                api.getTeacherCourseProfiles(teacher?.school?.id, teacher.id)
              ),
              tap((profiles) => this._slTeacherCourseProfiles[teacher.id] = profiles ?? []),
              map(() => teacher)
            )
          ),
          toArray()
        );
      }),
      tap((teachersProfiles) => {
        this._slTeacherProfiles = {}
        teachersProfiles?.forEach((teacherProfile) =>
          this._slTeacherProfiles[teacherProfile.id] = teacherProfile
        )
      }),
    );
  }

  private mapCourseCodeToProductCode(courseCode?: string | null): string {
    return CourseProductMapper.toProductCode(courseCode);
  }

  filterWorktimeByEventDate(date: Date) {
    const asTime = Dates.roundDateIntoMiddleOfClick(date).getTime();
    const now = new Date().getTime();
    const lessonDuration = StudentMainCalendarComponent.LESSON_DURATION_MS;
    return this.availableWorktimes
      .filter((wt) =>
        wt.events.some(
          (ev) =>
            asTime >= ev.eventDate.getTime() &&
            asTime + lessonDuration <= ev.eventDate.getTime() + ev.duration
        )
      )
      .filter(wt => wt.details.overtake == null || asTime - now > wt.details.overtake );
  }

  openLessonScheduleDialog(date: Date) {
    if (this.credits === 0) {
      this.noCreditsModal.show();
      return;
    }
    this._slPreferredTeacher = null;
    this._slScheduleDate = Dates.roundDateIntoMiddleOfClick(date);
    this.scheduleDateEmitter.emit(this._slScheduleDate)
    this._slScheduleFinishDate = new Date();
    this._slScheduleFinishDate.setTime(
      this._slScheduleDate.getTime() + StudentMainCalendarComponent.LESSON_DURATION_MS
    );
    this._slShowTeachers = true;
    this._slShowAll = true;
    if (this.teachersIdFilter && this.teachersIdFilter.length > 0) {
      this._slShowAll = false;
      this._slPreferredTeacher = this._slTeacherIds?.[0] ?? null;
    }
    // choose the teacher when is only one and not yet selected
    if (!this._slPreferredTeacher && this._slTeacherIds?.length === 1) {
      this._slPreferredTeacher = this._slTeacherIds[0];
    }
    this.scheduleLessonModal.show();
  }

  getTeacherPhoto(teacherId: number) {
    const teacherProfile = this._slTeacherProfiles[teacherId];
    return this.personContactCache.getPhotoUrlSync(teacherProfile?.person?.id);
  }

  getInitials(teacherId: number) {
    const teacherProfile = this._slTeacherProfiles[teacherId];

    if (!teacherProfile) {
      return null;
    }
    return PersonUtils.getInitials(teacherProfile.person.details);
  }

  getTeacherName(teacherId: number) {
    const teacherProfile = this._slTeacherProfiles[teacherId];
    if (!teacherProfile || !teacherProfile.person) {
      return '';
    }
    return PersonUtils.getPersonName(teacherProfile.person.details);
  }

  getTeacherDescription(teacherId: number) {
    const profiles = this._slTeacherCourseProfiles[teacherId];
    if (!profiles || profiles.length === 0) {
      return '';
    }
    const courseProfile = profiles.find(
      (p) => this.mapCourseCodeToProductCode(p?.course?.code) === this._langCode
    );
    if (!courseProfile) {
      return '';
    }
    return courseProfile.description ?? '';
  }

  scheduleLesson() {
    this.loading = true;
    const lessonSchedule = this.prepareLessonSchedule();
    if (!lessonSchedule) {
      this.loading = false;
      this._errorMessage = 'Unable to reserve lesson. Missing required lesson data.';
      this.reserveErrorModal.show();
      return;
    }

    this.ensureSchoolId().pipe(
      switchMap((schoolId) => this.apiProvider.colV3().pipe(
        switchMap((api) => api.reserveColLessonSchedule(schoolId, this._studentId, lessonSchedule))
      ))
    )
      .pipe(
        tap(_ => {
          this.loadCalendarEvents();
          this.appEvents.credits(this._langCode).next(this.credits - 1);
          this.appEvents.scheduleReserved.next();
        }),
        switchMap((response) => {
          const lessonId =
            response?.events?.[0]?.eventData?.lesson?.lessonId ??
            response?.schedule?.id;
          if (!lessonId) {
            return of(null);
          }
          return this.apiProvider.colV3().pipe(
            switchMap((api) => api.getLesson(lessonId, 'student-preview', this._studentId)),
            tap((lesson) => {
              const lessonType = lesson?.type;
              if (lessonType === LessonsV3.LessonType.Prova) {
                this.router.navigateByUrl(
                  `student/col-lessons/school/1/student/${this._studentId}/lesson/${lessonId}`
                ).then()
              }
            }),
            catchError(() => of(null))
          );
        })
      )
      .subscribe({
        error: (e) => {
          this.loading = false;
          this.handleReservationError(e);
        },
      });
    this.scheduleLessonModal.hide();
  }

  prepareLessonSchedule(): SimpleLessonScheduleRequestV3 | null {
    if (
      !this._slSelectedWorktimes?.length ||
      !this.requiredCompetences?.length ||
      !this.currentCourseCode ||
      !this._langCode ||
      !this._slScheduleDate ||
      !this._slPreferredTeacher
    ) {
      return null;
    }
    const teacherAvailableCompetences = this._slSelectedWorktimes
      .map( wt => wt.details.competences)
      .reduce((acc, x) => acc.concat(x))
      .map((competence) => competence.code);
    const teachersUniqueCompetences = Array.from(
      new Set(teacherAvailableCompetences)
    );
    const scheduleCompetence = teachersUniqueCompetences.find(
      (competenceCode) => this.requiredCompetences.includes(competenceCode)
    );
    if (!scheduleCompetence) {
      return null;
    }
    return {
      name: '',
      competence: {code: scheduleCompetence},
      course: {code: this.currentCourseCode, product: {code: this._langCode}},
      duration: 1000 * 60 * 30,
      time: {
        starting: this._slScheduleDate,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      teacher: {id: this._slPreferredTeacher},
    };
  }

  handleReservationError(error: ErrorBase) {
    if (
      !error ||
      !error.errorCode ||
      error.errorCode !== 'INSUFFICIENT_RESOURCE'
    ) {
      return;
    }
    this.loadCalendarEvents();
    this._errorMessage = error.userMessage;
    this.reserveErrorModal.show();
  }

  isCourseActive() {
    return !!this.studentProductContext
  }

  private getWeekRange(): {weekStart: Date; weekEnd: Date} {
    const weekStart = Utils.getWeekStartDate(this._focusDate);
    const weekEnd = new Date(weekStart.getTime() + 7 * StudentMainCalendarComponent.DAY_MS);
    return {weekStart, weekEnd};
  }

  private deferUiUpdate(action: () => void): void {
    Promise.resolve().then(action);
  }

  private triggerReload(loadCredits: boolean): void {
    this.requiredCompetences = null;
    this.deferUiUpdate(() => {
      this.loadCalendarEvents();
      if (loadCredits) {
        this.subscribeForCredits();
      }
      this.refreshBaseData(loadCredits).subscribe({
        error: () => {
          this.loading = false;
        },
      });
    });
  }
}
